"use client";

import { useTranslations } from "next-intl";

import { HelpLink } from "@/modules/help/components/help-link";
import { logger } from "@/lib/logger";

import { useWidget } from "../hooks/use-widget";
import { useWidgetAction } from "../hooks/use-widget-action";
import { ClockSettingsSheet } from "./clock-settings-sheet";
import { WidgetCard } from "./widget-card";
import { WidgetOpenButton } from "./widget-open-link";
import { canRenderPayload, WidgetRenderer } from "./widget-renderer";

import type { WidgetDefinition } from "@/types";

// Help for cards about the viewer's own pay; it never displaces a ↗ link.
const HELP_TOPICS: Record<string, string> = {
  my_salaries: "your-pay#my-checks",
  gp_targets: "your-pay#gross-profit-targets",
};

/**
 * One card, fetching itself.
 *
 * This exists because the query is per widget: a hook can't be called in a loop
 * from the parent, so each card has to be its own component. That is the entire
 * reason it isn't still the `renderWidget` closure inside Dashboard.
 */
export function DashboardWidget({
  definition,
  params,
  compact = false,
}: {
  definition: WidgetDefinition;
  params?: Record<string, unknown>;
  /** Header-strip tile: tighter chrome, no title row. */
  compact?: boolean;
}) {
  const t = useTranslations("Dashboard");
  const { result, isPending, refetch } = useWidget(definition, params);
  const runAction = useWidgetAction();

  // A payload this bundle has no renderer for (backend ahead of frontend) would
  // otherwise show as an empty titled card. Drop it instead.
  if (result?.status === "ok" && !canRenderPayload(result.payload)) {
    logger.warn("[dashboard] no renderer for widget", definition.key);
    return null;
  }

  const payload = result?.status === "ok" ? result.payload : undefined;

  // The corner holds one control (clock settings, the open action, help, or WidgetCard's ↗),
  // picked here because the renderer sits in the body and can't reach the header.
  // A local, so the narrowing survives into the closure below.
  const viewAllAction =
    payload?.type === "list" ? payload.view_all_action : null;
  const viewAllHref = payload?.type === "list" ? payload.view_all_href : null;

  const action =
    payload?.type === "clocks" ? (
      <ClockSettingsSheet payload={payload} />
    ) : viewAllAction ? (
      // Chat: the same ↗ affordance, but it opens a panel rather than navigating,
      // because there is no route to navigate to.
      <WidgetOpenButton
        onClick={() => runAction(viewAllAction)}
        label={t("viewAll")}
      />
    ) : definition.key in HELP_TOPICS && !viewAllHref ? (
      <HelpLink topic={HELP_TOPICS[definition.key]} />
    ) : null;

  return (
    <WidgetCard
      definition={definition}
      result={result}
      isLoading={isPending}
      onRetry={() => void refetch()}
      compact={compact}
      viewAllHref={viewAllHref}
      action={action}
    >
      {result?.status === "ok" ? (
        <WidgetRenderer payload={result.payload} />
      ) : null}
    </WidgetCard>
  );
}
